import { Box, Divider } from '@mui/material';
import log from 'loglevel';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import MultiSelect from '../../../../components/MultiSelect';
import TableFiltersItems from '../../../../components/Table/TableRenderFiltersItems';
import TableSearch from '../../../../components/Table/TableSearch';
import { MATCH_CANDIDATES_STATUS } from '../../../../consts/enums';
import { useGetRunIds } from '../../../../hooks/query/useMatchFlow';
import useDebounce from '../../../../hooks/useDebounce';
import { ActiveFilters } from '../../../../hooks/useTablePageParams';
import { FilterItem } from '../../../../interfaces/filterOptions.interface';
import STRING_KEYS from '../../../../language/keys';
import { createActiveFilterObject } from '../../../../utils';
import { FILTER_QUERY } from '../query';
import { style } from './style';

const TAG = '[CANDIDATE_MATCH_TABLE_FILTERS]';

type CandidateMatchTableFiltersProps = {
  onFilterChange: (filterName: string,  newValue: string[]) => void,
  activeFilters: ActiveFilters,
  accountId: string,
  positionId: string,
}

export const CandidateMatchTableFilters = ({
  onFilterChange,
  activeFilters,
  accountId,
  positionId,
}: CandidateMatchTableFiltersProps):JSX.Element => {
  const { t: translate } = useTranslation();
  const [runIdQuery, setRunIdQuery] = useState('');
  const { debouncedValue: debouncedRunIdQuery } = useDebounce(runIdQuery);
  const { filterOptions, isLoading } = useGetRunIds(
    accountId,
    positionId,
    debouncedRunIdQuery,
  );

  const handleSearchChange = (value: string) => {
    onFilterChange(FILTER_QUERY.LINKEDIN_URL, [value]);
  };

  const handleChangeRunId = (value: string[]) => {
    onFilterChange(FILTER_QUERY.RUN_ID, value);
  };
  const handleChangeRunStatus = (value: string[]) => {
    onFilterChange(FILTER_QUERY.RUN_STATUS, value);
  };

  const handleRunIdQueryChange = (value: string) => {
    setRunIdQuery(value);
  };

  const deleteFilterItem = (
    filterName: string,
    filterItem: FilterItem,
  ): string[] => {
    return activeFilters[filterName].filter((item: string) => {
      return item !== filterItem.value;
    });
  };

  const handleDeleteFilterClick = (filterItem: FilterItem) => {
    switch (filterItem.field) {
      case FILTER_QUERY.LINKEDIN_URL: {
        handleSearchChange('');
        break;
      }
      case FILTER_QUERY.RUN_ID: {
        handleChangeRunId(
          deleteFilterItem(FILTER_QUERY.RUN_ID, filterItem),
        );
        break;
      }
      case FILTER_QUERY.RUN_STATUS: {
        handleChangeRunStatus(
          deleteFilterItem(FILTER_QUERY.RUN_STATUS, filterItem),
        );
        break;
      }
      default:
        log.warn(TAG, 'unknown filter type', { type: filterItem.field });
        break;
    }
  };

  const matchFlowStatusesOption = [
    {
      label: 'Failed',
      value: MATCH_CANDIDATES_STATUS.PROMOTION_FAILED,
    },
    {
      label: 'Pending review',
      value: MATCH_CANDIDATES_STATUS.PENDING_REVIEW,
    },
    {
      label: 'Pending promotion',
      value: MATCH_CANDIDATES_STATUS.PENDING_PROMOTION,
    },
    {
      label: 'In progress',
      value: MATCH_CANDIDATES_STATUS.PROMOTION_IN_PROGRESS,
    },
  ];
 
  const memoizedActiveFilters = useMemo((): FilterItem[] => {
    const filters: FilterItem[] = [];

    if (activeFilters[FILTER_QUERY.RUN_ID].length > 0) {
      activeFilters[FILTER_QUERY.RUN_ID].forEach((item: string) => {
        if (item.length > 0) {
          filters.push(
            createActiveFilterObject(
              FILTER_QUERY.RUN_ID,
              translate(STRING_KEYS.RUN_ID),
              item,
            ),
          );
        }
      });
    }

    if (activeFilters[FILTER_QUERY.RUN_STATUS].length > 0) {
      activeFilters[FILTER_QUERY.RUN_STATUS].forEach((item: string) => {
        if (item.length > 0) {
          filters.push(
            createActiveFilterObject(
              FILTER_QUERY.RUN_STATUS,
              translate(STRING_KEYS.RUN_STATUS),
              item,
              translate(STRING_KEYS.PROMOTING_STATUS[item]),
            ),
          );
        }
      });
    }
    if (activeFilters[FILTER_QUERY.LINKEDIN_URL].length > 0) {
      activeFilters[FILTER_QUERY.LINKEDIN_URL].forEach((item: string) => {
        if (item.length > 0) {
          filters.push(
            createActiveFilterObject(
              FILTER_QUERY.LINKEDIN_URL,
              translate(STRING_KEYS.FILTER_LABEL_TITLE),
              item,
            ),
          );
        }
      });
    }

    return filters;
  }, [activeFilters, translate]);

  return (
    <Box>
      <Divider />
      <TableSearch
        handleSearch={ handleSearchChange }
      />
      <Divider />
      <Box sx={ style.Box.sx }>
        <MultiSelect
          label={ translate(STRING_KEYS.RUN_ID) }
          onChange={ handleChangeRunId }
          options={ filterOptions }
          value={ activeFilters[FILTER_QUERY.RUN_ID] }
          autoComplete
          autoCompleteOnChange={  handleRunIdQueryChange }
          autoCompleteValue={ runIdQuery }
          isLoading={ isLoading }
        />
        <MultiSelect
          label={ translate(STRING_KEYS.STATUS) }
          onChange={ handleChangeRunStatus }
          options={ matchFlowStatusesOption }
          value={ activeFilters[FILTER_QUERY.RUN_STATUS] }
        />
      </Box>
      <Divider />
      <TableFiltersItems
        filters={ memoizedActiveFilters }
        onDelete={ handleDeleteFilterClick }
      />
    </Box>
  );
};
